// The MULTI-METRIC trend chart — several metrics on ONE canvas, which is the
// capability the single-metric chart deliberately does not have.
//
// ⚠️ ONE COMPONENT, BOTH DASHBOARDS. This started life inside GoogleAdsTrend and
// was lifted here rather than copied, for the same reason ReportingMiniChart /
// ChartStyleToggle / ReportingChartGrid live in this module: two chart stacks
// drift, and a chart that draws a client's numbers is the worst place for a
// drift nobody notices. `GoogleAdsTrend` is now a thin adapter over this, and
// the Meta page has one beside it — see `buildMultiTrendData`.
//
// ⚠️ IT TAKES ALREADY-RESOLVED SERIES, not a summary. The two dashboards have
// entirely different metric vocabularies, bucketing and money rules, so each
// hands in `{ value, formatted }` per bucket and the chart draws exactly that.
// That is what keeps the honesty rules WHERE THEY ALREADY LIVE:
//
//   · PER-BUCKET RATIOS — a weekly CTR is the week's clicks ÷ the week's
//     impressions, never the mean of seven daily CTRs. Both adapters recompute
//     from that bucket's summed totals; this component never divides anything.
//   · PER-CURRENCY MONEY — the plotted `value` is a SHAPE and may be a sum
//     across currencies, but the tooltip shows the adapter's own authoritative
//     `formatted` string ("A$60,176 · NZ$28,927"), never a blended total.
//   · GAPS ARE NULL — an unmeasured bucket is never plotted as 0. A zero
//     impression share would draw a line to the floor and read as "we lost the
//     whole auction that day" when Google reported nothing.
//     ⚠️ Whether the line JOINS ACROSS such a bucket is `connectNulls`, and the
//     default is TRUE — i.e. it does join. An earlier version of this comment
//     said connectNulls produced a break; it is the opposite. The dashboards'
//     behaviour is unchanged — only the description was wrong.

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatBucketLabel, type TrendGrouping } from './chartDate';

/** Distinct hues, stable per series index. */
const COLORS = ['#16a34a', '#2563eb', '#d97706', '#dc2626', '#7c3aed', '#0891b2'];

export interface MultiTrendPoint {
  /** What is plotted. `null` is a GAP — never substitute 0. */
  value: number | null;
  /** The AUTHORITATIVE figure for the tooltip, formatted by the dashboard. */
  formatted: string;
}

export interface MultiTrendSeries {
  key: string;
  label: string;
  /** One point per bucket, in `buckets` order. */
  points: MultiTrendPoint[];
  /**
   * How to draw it. Default 'line' — every pre-existing caller renders lines and
   * is unaffected. (Round 2: the client portal needs a per-series toggle, the
   * same control the social explorer already offers.)
   */
  type?: 'line' | 'bar';
  /**
   * ⚠️ WHICH AXIS. Default 'left'. The secondary axis exists because plotted
   * magnitudes differ wildly — a CPL around $10 beside a spend around $1,500
   * flattens the CPL onto the floor and makes it unreadable. The AM pulse's
   * posts-published series set the precedent. Callers that pass nothing keep a
   * single left axis exactly as before.
   */
  axis?: 'left' | 'right';
  /**
   * The series this one CONTINUES: same colour, and no second legend entry.
   * Lets one logical series be drawn as several segments — a portal CPL line
   * breaks at unmeasured buckets but joins across known zero-lead days, which a
   * single `connectNulls` boolean cannot express.
   */
  continuationOf?: string;
}

export interface MultiTrendData {
  /** The X categories — bucket ids (an ISO date, or 'YYYY-MM' when monthly). */
  buckets: string[];
  grouping: TrendGrouping;
  series: MultiTrendSeries[];
  /**
   * Whether a line joins across a null. Default TRUE — what every pre-existing
   * caller gets.
   *
   * ⚠️ A caller needing SOME nulls joined and others broken cannot express it
   * here; that is all-or-nothing by nature. It splits its series into
   * `continuationOf` segments instead and leaves this true.
   */
  connectNulls?: boolean;
}

/** The recharts row shape: one row per bucket, one numeric key per series. */
interface Row {
  bucket: string;
  __formatted: Record<string, string>;
  [metricKey: string]: unknown;
}

function toRows(data: MultiTrendData): Row[] {
  return data.buckets.map((bucket, i) => {
    const row: Row = { bucket, __formatted: {} };
    for (const s of data.series) {
      const point = s.points[i];
      row[s.key] = point ? point.value : null;
      row.__formatted[s.key] = point ? point.formatted : '—';
    }
    return row;
  });
}

/**
 * Colour per series, with a continuation inheriting its parent's. Only
 * legend-bearing series take the next hue, so a segmented series does not burn
 * several colours and leave the next real one looking unrelated to its legend key.
 */
export function seriesColors(series: readonly MultiTrendSeries[]): Record<string, string> {
  const out: Record<string, string> = {};
  let next = 0;
  for (const s of series) {
    if (s.continuationOf) continue;
    out[s.key] = COLORS[next % COLORS.length] as string;
    next += 1;
  }
  for (const s of series) {
    if (!s.continuationOf) continue;
    out[s.key] = out[s.continuationOf] ?? (COLORS[0] as string);
  }
  return out;
}

export function MultiTrendChart({
  title,
  data,
  loading,
}: {
  title: string;
  data: MultiTrendData;
  loading: boolean;
}): React.JSX.Element {
  const rows = toRows(data);
  const colors = seriesColors(data.series);
  const connect = data.connectNulls ?? true;
  // Only render the right axis when something asks for it, so the default
  // single-axis look is unchanged for existing callers.
  const hasRightAxis = data.series.some((s) => s.axis === 'right');
  const empty = rows.length === 0 || data.series.length === 0;

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <h2 className="mb-3 text-sm font-semibold text-foreground">{title}</h2>
      {loading && empty ? (
        <div className="h-64 w-full animate-pulse rounded bg-muted" />
      ) : empty ? (
        <p className="py-16 text-center text-sm text-muted-foreground">No data in this range.</p>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={rows} margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
            <XAxis
              dataKey="bucket"
              tick={{ fontSize: 11 }}
              tickMargin={8}
              tickFormatter={(v) => formatBucketLabel(String(v), data.grouping)}
            />
            <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={56} />
            {hasRightAxis && (
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={56} />
            )}
            <Tooltip content={<MultiTrendTooltip data={data} />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {data.series.map((s) =>
              s.type === 'bar' ? (
                <Bar
                  key={s.key}
                  yAxisId={s.axis ?? 'left'}
                  dataKey={s.key}
                  // ⚠️ A continuation carries no name and no legend type, so
                  // recharts emits no second legend entry for it.
                  name={s.continuationOf ? undefined : s.label}
                  legendType={s.continuationOf ? 'none' : undefined}
                  fill={colors[s.key]}
                  radius={[2, 2, 0, 0]}
                  maxBarSize={28}
                  isAnimationActive={false}
                />
              ) : (
                <Line
                  key={s.key}
                  yAxisId={s.axis ?? 'left'}
                  type="monotone"
                  dataKey={s.key}
                  name={s.continuationOf ? undefined : s.label}
                  legendType={s.continuationOf ? 'none' : undefined}
                  stroke={colors[s.key]}
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                  // A bucket with no measurement is a GAP, not a zero. Whether
                  // the line JOINS across one is the caller's call — see
                  // MultiTrendData.connectNulls.
                  connectNulls={connect}
                />
              ),
            )}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </section>
  );
}

interface TooltipProps {
  active?: boolean;
  label?: string;
  payload?: Array<{ payload: Row }>;
  data: MultiTrendData;
}

/** Shows each series' AUTHORITATIVE figure, not the chart's plotted proxy. */
function MultiTrendTooltip({
  active,
  label,
  payload,
  data,
}: TooltipProps): React.JSX.Element | null {
  if (!active || !payload || payload.length === 0) return null;
  const formatted = payload[0]?.payload.__formatted;
  if (!formatted) return null;
  return (
    <div className="rounded-md border border-border bg-popover p-2 text-xs shadow-md">
      <div className="mb-1 font-medium">
        {formatBucketLabel(String(label ?? ''), data.grouping)}
      </div>
      {data.series
        // A continuation is the same logical series drawn in pieces; listing it
        // again would repeat every row of the tooltip.
        .filter((s) => !s.continuationOf)
        .map((s) => (
          <div key={s.key} className="flex justify-between gap-4">
            <span className="text-muted-foreground">{s.label}</span>
            <span className="tabular-nums">{formatted[s.key] ?? '—'}</span>
          </div>
        ))}
    </div>
  );
}
